'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Trans } from '@kit/ui/trans';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { SectionHeader } from '~/(tenant)/_components/native/_primitives';
import Link from '~/(tenant)/_components/tenant-link';
import type {
  ActivityMetric,
  ActivitySectionConfig,
} from '~/(tenant)/_lib/schema/native-home-config.schema';
import {
  type ActivityKind,
  type MonthActivity,
  dayKeyFor,
  monthGrid,
} from '~/(tenant)/activity/_lib/member-activity';
import { getCurrentMonthActivityAction } from '~/(tenant)/activity/_lib/server/activity-server-actions';

const METRIC_KIND: Record<ActivityMetric, ActivityKind> = {
  visits: 'visit',
  classes: 'class',
};

const METRIC_LABELS: Record<
  ActivityMetric,
  { i18nKey: string; defaults: string }
> = {
  visits: { i18nKey: 'common:activity.counts.visits', defaults: 'Visits' },
  classes: {
    i18nKey: 'common:activity.counts.classes',
    defaults: 'Classes attended',
  },
};

/**
 * "Recent activity" card: this month's headline count with a Sunday-first
 * dot per day (filled = active, grey = past, small = upcoming). Hidden when
 * signed out. An unset metric — or 'visits' on a tenant without member QR
 * check-in — falls back to whatever the server says can be counted.
 */
export function ActivitySection({
  config,
  accountId,
}: {
  config: ActivitySectionConfig;
  accountId: string;
}) {
  const client = useSupabase();

  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
    staleTime: 60_000,
  });

  const userId = session?.user?.id;

  const { data } = useQuery({
    queryKey: ['native-home-activity', accountId, userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: () => getCurrentMonthActivityAction(undefined),
  });

  if (!userId || !data) return null;

  const metric: ActivityMetric =
    data.showVisits && config.metric !== 'classes' ? 'visits' : 'classes';

  return <ActivityCard activity={data.activity} metric={metric} />;
}

export function ActivityCard({
  activity,
  metric,
}: {
  activity: MonthActivity;
  metric: ActivityMetric;
}) {
  const t = useNativeTokens();
  const { t: translate } = useTranslation();
  const kind = METRIC_KIND[metric];
  const count = activity.counts[kind];

  return (
    <section style={{ paddingTop: 32 }} data-test="native-activity-section">
      <SectionHeader
        title={
          <Trans
            i18nKey="common:activity.recentTitle"
            defaults="Recent activity"
          />
        }
        action={{
          label: <Trans i18nKey="common:activity.seeAll" defaults="See all" />,
          href: '/activity',
        }}
      />

      <div style={{ marginTop: 14, paddingInline: 16 }}>
        <Link
          href="/activity"
          aria-label={translate('common:activity.cardAria', {
            defaultValue: '{{count}} {{metric}} this month. View activity',
            count,
            metric: translate(METRIC_LABELS[metric].i18nKey, {
              defaultValue: METRIC_LABELS[metric].defaults,
            }),
          })}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: 20,
            borderRadius: t.radiusLg,
            border: `1px solid ${t.line}`,
            backgroundColor: t.surface,
            color: t.foreground,
            textDecoration: 'none',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontFamily: t.fontSans,
                fontSize: 17,
                fontWeight: 600,
              }}
            >
              <Trans {...METRIC_LABELS[metric]} />
            </div>
            <div
              style={{
                fontFamily: t.fontSans,
                fontSize: 13,
                color: t.muted,
                marginTop: 2,
              }}
            >
              <Trans
                i18nKey="common:activity.thisMonth"
                defaults="This month"
              />
            </div>
            <div
              data-test="native-activity-count"
              style={{
                fontFamily: t.fontDisplay,
                fontSize: 40,
                fontWeight: 600,
                lineHeight: 1,
                marginTop: 18,
              }}
            >
              {count}
            </div>
          </div>

          <MonthDots activity={activity} kind={kind} />
        </Link>
      </div>
    </section>
  );
}

function MonthDots({
  activity,
  kind,
}: {
  activity: MonthActivity;
  kind: ActivityKind;
}) {
  const t = useNativeTokens();
  const { leadingBlanks, daysInMonth } = monthGrid(activity.month);

  return (
    <div
      aria-hidden
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 12px)',
        gap: 6,
        flexShrink: 0,
      }}
    >
      {Array.from({ length: leadingBlanks }, (_, i) => (
        <span key={`blank-${i}`} />
      ))}
      {Array.from({ length: daysInMonth }, (_, i) => {
        const key = dayKeyFor(activity.month, i + 1);
        const isFuture = key > activity.today;
        const isActive = activity.days[key]?.includes(kind) ?? false;
        const size = isFuture ? 5 : 12;

        return (
          <span
            key={key}
            style={{
              justifySelf: 'center',
              alignSelf: 'center',
              width: size,
              height: size,
              borderRadius: 999,
              backgroundColor: isActive ? t.accent : t.border,
            }}
          />
        );
      })}
    </div>
  );
}
